import React, { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw,
  Search,
  X,
  Save,
  Printer,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';

const PAGE_SIZE = 100;

const CreateReturn = () => {
  const { language } = useLanguage();
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadAllMode, setLoadAllMode] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [voucherSearch, setVoucherSearch] = useState('');
  const [companyProfile, setCompanyProfile] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [clientReturn, setClientReturn] = useState({
    RefNo: '',
    EmployeeId: '',
    Date: new Date().toISOString().split('T')[0],
    TotalOrders: 0,
  });
  const [filters, setFilters] = useState({
    ClientId: '',
    EmirateId: '',
    AreaId: '',
    EmployeeId: '',
  });
  const [selectAll, setSelectAll] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveAndPrint, setSaveAndPrint] = useState(false);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    const loadCompanyProfile = async () => {
      try {
        const response = await MainService.GetCompanyProfile();
        setCompanyProfile(response);
      } catch (err) {
        console.error('Error loading company profile:', err);
      }
    };
    loadCompanyProfile();
  }, []);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [employeesResp, emiratesResp, areasResp] = await Promise.all([
          MainService.EmployeesInDropDown(),
          MainService.EmiratesInDropDown(),
          MainService.AreasInDropDown(),
        ]);
        setEmployees(employeesResp || []);
        setEmirates(emiratesResp || []);
        setAreas(areasResp || []);
      } catch (err) {
        console.error('Error loading dropdowns:', err);
      }
    };
    loadDropdowns();
  }, []);

  const loadNextReturnNo = async () => {
    try {
      const response = await MainService.GetNextClientReturnNo();
      if (response) {
        setClientReturn((prev) => ({ ...prev, RefNo: response }));
      }
    } catch (err) {
      console.error('Error loading next return number:', err);
    }
  };

  useEffect(() => {
    loadNextReturnNo();
  }, []);

  const recalcTotalOrders = (clients) =>
    clients.reduce((sum, client) => sum + (client.ReturnCount || 0), 0);

  const getFilterRequestData = () => ({
    EmirateId: filters.EmirateId || 0,
    AreaId: filters.AreaId || 0,
    ClientId: filters.ClientId || 0,
    EmployeeId: filters.EmployeeId || 0,
  });

  const loadClients = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      setLoadAllMode(false);
      setSelectAll(false);

      const response = await MainService.ClientsInClientReturnList({
        ...getFilterRequestData(),
        Skip: (page - 1) * PAGE_SIZE,
        Take: PAGE_SIZE,
      });

      const data = response?.Data || [];
      setAllClients(data);
      setTotalCount(response?.Total || data.length);
      setCurrentPage(page);
      setClientReturn((prev) => ({ ...prev, TotalOrders: 0 }));
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Failed to load clients');
      setAllClients([]);
      setTotalCount(0);
      setClientReturn((prev) => ({ ...prev, TotalOrders: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const loadAllClients = async () => {
    try {
      setLoading(true);
      setError('');
      setSelectAll(false);

      let allClientsData = [];
      let skip = 0;
      let total = 0;
      let hasMore = true;
      const baseRequestData = getFilterRequestData();

      while (hasMore) {
        const response = await MainService.ClientsInClientReturnList({
          ...baseRequestData,
          Skip: skip,
          Take: PAGE_SIZE,
        });

        const batch = response?.Data || [];
        total = response?.Total || 0;
        allClientsData = [...allClientsData, ...batch];
        skip += batch.length;

        if (batch.length === 0 || allClientsData.length >= total) {
          hasMore = false;
        }
      }

      setAllClients(allClientsData);
      setTotalCount(total || allClientsData.length);
      setCurrentPage(1);
      setLoadAllMode(true);
      setClientReturn((prev) => ({ ...prev, TotalOrders: 0 }));
    } catch (err) {
      console.error('Error loading all clients:', err);
      setError('Failed to load all clients');
      setAllClients([]);
      setTotalCount(0);
      setClientReturn((prev) => ({ ...prev, TotalOrders: 0 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const filteredClients = useMemo(() => {
    if (!searchKeyword.trim()) return allClients;
    const keyword = searchKeyword.toLowerCase();
    return allClients.filter(
      (client) =>
        (client.Name || '').toLowerCase().includes(keyword) ||
        String(client.ClientNo ?? '').toLowerCase().includes(keyword) ||
        (client.MobileNo1 || '').toLowerCase().includes(keyword) ||
        (client.LocationName || '').toLowerCase().includes(keyword) ||
        (client.EmirateName || '').toLowerCase().includes(keyword) ||
        (client.AreaName || '').toLowerCase().includes(keyword)
    );
  }, [allClients, searchKeyword]);

  const handleFilterClients = () => {
    if (filters.EmployeeId) {
      setClientReturn((prev) => ({ ...prev, EmployeeId: filters.EmployeeId }));
    }
    loadClients(1);
  };

  const handleRefresh = async () => {
    setFilters({ ClientId: '', EmirateId: '', AreaId: '', EmployeeId: '' });
    setSearchKeyword('');
    setSelectAll(false);
    setClientReturn({
      RefNo: '',
      EmployeeId: '',
      Date: new Date().toISOString().split('T')[0],
      TotalOrders: 0,
    });
    await loadNextReturnNo();
    loadClients(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage || loading || loadAllMode) return;
    loadClients(page);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    const filteredIds = new Set(filteredClients.map((c) => c.Id));
    const updatedClients = allClients.map((client) => {
      if (!filteredIds.has(client.Id)) return client;
      if (newSelectAll) {
        const updatedOrders = (client.Orders || []).map((order) => ({ ...order, IsChecked: true }));
        return {
          ...client,
          checked: true,
          Orders: updatedOrders,
          ReturnCount: updatedOrders.length,
        };
      }
      const updatedOrders = (client.Orders || []).map((order) => ({ ...order, IsChecked: false }));
      return {
        ...client,
        checked: false,
        Orders: updatedOrders,
        ReturnCount: 0,
      };
    });

    setAllClients(updatedClients);
    setClientReturn((prev) => ({ ...prev, TotalOrders: recalcTotalOrders(updatedClients) }));
  };

  const handleToggleClient = (clientId) => {
    const updatedClients = allClients.map((client) => {
      if (client.Id !== clientId) return client;
      const newChecked = !client.checked;
      if (newChecked) {
        const updatedOrders = (client.Orders || []).map((order) => ({ ...order, IsChecked: true }));
        return {
          ...client,
          checked: true,
          Orders: updatedOrders,
          ReturnCount: updatedOrders.length,
        };
      }
      const updatedOrders = (client.Orders || []).map((order) => ({ ...order, IsChecked: false }));
      return {
        ...client,
        checked: false,
        Orders: updatedOrders,
        ReturnCount: 0,
      };
    });

    setAllClients(updatedClients);
    setClientReturn((prev) => ({ ...prev, TotalOrders: recalcTotalOrders(updatedClients) }));
  };

  const handleToggleOrder = (orderId) => {
    if (!selectedClient) return;

    const updatedClients = allClients.map((client) => {
      if (client.Id !== selectedClient.Id) return client;
      const updatedOrders = (client.Orders || []).map((order) => {
        if (order.Id === orderId) {
          return { ...order, IsChecked: !order.IsChecked };
        }
        return order;
      });
      const returnCount = updatedOrders.filter((o) => o.IsChecked).length;
      return {
        ...client,
        Orders: updatedOrders,
        ReturnCount: returnCount,
        checked: returnCount > 0,
      };
    });

    setAllClients(updatedClients);
    setClientReturn((prev) => ({ ...prev, TotalOrders: recalcTotalOrders(updatedClients) }));

    const updatedClient = updatedClients.find((c) => c.Id === selectedClient.Id);
    if (updatedClient) setSelectedClient(updatedClient);
  };

  const handleViewOrders = (client) => {
    setSelectedClient(client);
    setShowOrdersModal(true);
  };

  const handleOpenSaveModal = (shouldPrint = false) => {
    const hasSelected = allClients.some((c) => (c.ReturnCount || 0) > 0);
    if (!hasSelected) {
      setError('Please select orders for return');
      return;
    }
    setSaveAndPrint(shouldPrint);
    setShowSaveModal(true);
  };

  const handleSaveReturn = async () => {
    if (!clientReturn.EmployeeId) {
      setError('Select Employee');
      return;
    }
    if (clientReturn.TotalOrders === 0) {
      setError('Please select orders for return');
      return;
    }

    try {
      setLoading(true);
      const currentDate = new Date(clientReturn.Date);
      const returnData = {
        RefNo: clientReturn.RefNo,
        EmployeeId: clientReturn.EmployeeId,
        Date: clientReturn.Date,
        TotalOrders: clientReturn.TotalOrders,
        Month: currentDate.getMonth() + 1,
        Day: currentDate.getDate(),
        Year: currentDate.getFullYear(),
        ClientReturnClients: allClients
          .filter((c) => (c.ReturnCount || 0) > 0)
          .map((client) => ({
            TotalOrders: client.ReturnCount,
            ClientId: client.Id,
            ClientReturnClientOrders: (client.Orders || [])
              .filter((order) => order.IsChecked)
              .map((order) => ({ OrderId: order.Id })),
          })),
      };

      const response = await MainService.SaveClientReturn(returnData);
      if (response?.Status === 'Success') {
        setSuccess('Return saved successfully');
        setShowSaveModal(false);
        if (saveAndPrint && response.ClientReturnId) {
          await handlePrintReturn(response.ClientReturnId);
        }
        handleRefresh();
      } else {
        setError(response?.Status || 'Failed to save return');
      }
    } catch (err) {
      console.error('Error saving return:', err);
      setError('Failed to save return');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReturn = async (returnId) => {
    try {
      const response = await MainService.PrintClientReturn(returnId);
      if (response) {
        window.open(`${getApiBaseUrl()}ReportFiles/${response}`, '_blank');
      }
    } catch (err) {
      console.error('Error printing return:', err);
      setError('Failed to print return');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  const filteredOrders = useMemo(() => {
    if (!selectedClient) return [];
    if (!voucherSearch.trim()) return selectedClient.Orders || [];
    const keyword = voucherSearch.toLowerCase();
    return (selectedClient.Orders || []).filter((order) =>
      (order.VoucherNo || '').toLowerCase().includes(keyword)
    );
  }, [selectedClient, voucherSearch]);

  const selectedOrderCount = useMemo(() => {
    if (!selectedClient) return 0;
    return (selectedClient.Orders || []).filter((o) => o.IsChecked).length;
  }, [selectedClient]);

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-24 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-[100] min-w-[300px]">
          <X className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="fixed top-24 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-[100] min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              <span>{getPageHeading(language, 'createReturn')}</span>
              <span className="inline-block ms-1" dir="ltr">
                {' '}
                — {clientReturn.RefNo || getPageHeading(language, 'newRef')}
              </span>
            </h1>
            <p className="text-sm text-gray-500 mt-1">Create client return from return-approved orders</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleOpenSaveModal(false)}
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> Save
            </button>
            <button
              onClick={() => handleOpenSaveModal(true)}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Printer className="w-4 h-4" /> Save & Print
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 mb-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Emirate</label>
            <select
              value={filters.EmirateId}
              onChange={(e) => setFilters((prev) => ({ ...prev, EmirateId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All</option>
              {emirates.map((emirate) => (
                <option key={emirate.Id} value={emirate.Id}>{emirate.Name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Area</label>
            <select
              value={filters.AreaId}
              onChange={(e) => setFilters((prev) => ({ ...prev, AreaId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All</option>
              {areas.map((area) => (
                <option key={area.Id} value={area.Id}>{area.Name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Responsible By</label>
            <select
              value={filters.EmployeeId}
              onChange={(e) => setFilters((prev) => ({ ...prev, EmployeeId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All</option>
              {employees.map((employee) => (
                <option key={employee.Id} value={employee.Id}>{employee.Name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2 flex items-end gap-2">
            <button onClick={handleFilterClients} disabled={loading} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">
              Filter
            </button>
            <button onClick={handleRefresh} disabled={loading} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50">
              Clear
            </button>
          </div>
          <div className="col-span-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder="Search clients..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              {searchKeyword && (
                <button onClick={() => setSearchKeyword('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                  <input type="checkbox" checked={selectAll} onChange={handleSelectAll} className="w-4 h-4" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">S.No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client Mobile No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Emirate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Area</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Orders</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Return Orders</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Options</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && filteredClients.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-12 text-center">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500">Loading clients...</p>
                  </td>
                </tr>
              ) : filteredClients.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-12 text-center text-gray-500">No clients found</td>
                </tr>
              ) : (
                filteredClients.map((client, index) => (
                  <tr key={client.Id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={client.checked || false}
                        onChange={() => handleToggleClient(client.Id)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {loadAllMode ? index + 1 : (currentPage - 1) * PAGE_SIZE + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{client.Name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{client.ClientNo || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{client.MobileNo1 || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{client.LocationName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{client.EmirateName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{client.AreaName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">{client.TotalOrders || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">{client.ReturnCount || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <button
                        onClick={() => handleViewOrders(client)}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        Orders
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalCount > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              {loadAllMode ? (
                <>Showing all <span className="font-semibold">{allClients.length}</span> of <span className="font-semibold">{totalCount}</span> clients</>
              ) : (
                <>
                  Showing{' '}
                  <span className="font-semibold">{(currentPage - 1) * PAGE_SIZE + (filteredClients.length ? 1 : 0)}</span>
                  {' - '}
                  <span className="font-semibold">{(currentPage - 1) * PAGE_SIZE + filteredClients.length}</span>
                  {' of '}
                  <span className="font-semibold">{totalCount}</span> clients
                </>
              )}
            </p>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {!loadAllMode && totalCount > PAGE_SIZE && (
                <>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={loading || currentPage <= 1}
                    className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Prev
                  </button>
                  <span className="text-sm text-gray-700 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={loading || currentPage >= totalPages}
                    className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}
              {!loadAllMode && totalCount > allClients.length && (
                <button
                  onClick={loadAllClients}
                  disabled={loading}
                  className="px-4 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 text-sm font-medium"
                >
                  Load All ({totalCount})
                </button>
              )}
              {loadAllMode && totalCount > PAGE_SIZE && (
                <button
                  onClick={() => loadClients(1)}
                  disabled={loading}
                  className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 text-sm font-medium"
                >
                  Use Pagination
                </button>
              )}
            </div>
          </div>
        )}

        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-center">
            <p className="text-lg font-semibold text-gray-900">
              Total Orders: <span className="text-primary">{clientReturn.TotalOrders}</span>
            </p>
          </div>
        </div>
      </div>

      {showSaveModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Save Return</h3>
              <button onClick={() => { setShowSaveModal(false); setSaveAndPrint(false); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
                <select
                  value={clientReturn.EmployeeId}
                  onChange={(e) => setClientReturn((prev) => ({ ...prev, EmployeeId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select Employee</option>
                  {employees.map((employee) => (
                    <option key={employee.Id} value={employee.Id}>{employee.Name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={clientReturn.Date}
                  onChange={(e) => setClientReturn((prev) => ({ ...prev, Date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => { setShowSaveModal(false); setSaveAndPrint(false); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button
                onClick={handleSaveReturn}
                disabled={loading || !clientReturn.EmployeeId}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 flex items-center gap-2"
              >
                {saveAndPrint ? <><Printer className="w-4 h-4" /> Save & Print</> : <><Save className="w-4 h-4" /> Save</>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showOrdersModal && selectedClient && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Orders — {selectedClient.Name}</h3>
              <button onClick={() => { setShowOrdersModal(false); setSelectedClient(null); setVoucherSearch(''); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Voucher Number</label>
              <input
                type="text"
                value={voucherSearch}
                onChange={(e) => setVoucherSearch(e.target.value)}
                placeholder="Search by voucher number..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    {!companyProfile?.IsBlockByOrderSelection && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Select</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Voucher No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Delivery Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Mobile No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Location | Emirate</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase border border-gray-300">Product Charge</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase border border-gray-300">Service Charge</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase border border-gray-300">VAT</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase border border-gray-300">Client Money</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Payment Type</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase border border-gray-300">Advance Charge</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase border border-gray-300">Advance Price</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase border border-gray-300">Free</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={16} className="px-4 py-4 text-center text-gray-500 text-sm">No orders found</td>
                    </tr>
                  ) : (
                    filteredOrders.map((order, index) => (
                      <tr key={order.Id} className="hover:bg-gray-50">
                        {!companyProfile?.IsBlockByOrderSelection && (
                          <td className="px-4 py-3 border border-gray-300">
                            <input
                              type="checkbox"
                              checked={order.IsChecked || false}
                              onChange={() => handleToggleOrder(order.Id)}
                              className="w-4 h-4"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3 text-sm border border-gray-300">{index + 1}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300">{order.VoucherNo || '-'}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300">{formatDate(order.DeliveryDate)}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300">{order.Customer?.Name || '-'}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300">{order.Customer?.MobileNo1 || '-'}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300">
                          {order.CustomerAddress?.Location?.Name || '-'} | {order.CustomerAddress?.Emirate?.Name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm border border-gray-300 text-right">AED {formatCurrency(order.ProductPrice)}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300 text-right">AED {formatCurrency(order.DeliveryCharge)}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300 text-right">AED {formatCurrency(order.TAX)}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300 text-right">AED {formatCurrency(order.ClientAmount)}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300">{order.PayType || '-'}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300 text-center">{order.IsAdvancePay ? 'Yes' : 'No'}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300 text-center">{order.IsAdvancePrice ? 'Yes' : 'No'}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300 text-center">{order.IsFree ? 'Yes' : 'No'}</td>
                        <td className="px-4 py-3 text-sm border border-gray-300">
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {order.Status?.Name || '-'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end">
              <div className="px-4 py-2 bg-gray-100 rounded-lg">
                <span className="text-sm text-gray-700">Selected Count: </span>
                <span className="text-sm font-semibold text-gray-900">{selectedOrderCount}</span>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => { setShowOrdersModal(false); setSelectedClient(null); setVoucherSearch(''); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CreateReturn;
